import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Users, Mail, Phone, Tag, Building2, CreditCard, AlertCircle, CheckCircle, Info } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const BookingForm = ({ location }) => {
  const [formData, setFormData] = useState({
    customerName: '',
    email: '',
    whatsappNumber: '',
    numberOfJumpers: 1,
    bookingDate: '',
    slotId: '',
    couponCode: '',
    agencyCode: '',
    paymentType: 'full'
  });

  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsByDate, setSlotsByDate] = useState({});
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(null);
  const [agencyInfo, setAgencyInfo] = useState(null);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);

  // Calculate pricing with proper null checks
  const pricePerJumper = location.pricePerJumper || 0;
  const baseAmount = pricePerJumper * formData.numberOfJumpers;
  const discountAmount = couponDiscount?.discountAmount || 0;
  const totalAmount = Math.max(0, baseAmount - discountAmount);
  const paymentAmount = formData.paymentType === 'partial' && location.partialPaymentEnabled
    ? (location.partialPaymentAmount || 0)
    : totalAmount;
  const pendingAmount = Math.max(0, totalAmount - paymentAmount);

  // Fetch available coupons and slots
  useEffect(() => {
    fetchAvailableCoupons();
  }, []);

  useEffect(() => {
    if (formData.bookingDate) {
      fetchAvailableSlots(formData.bookingDate);
    }
  }, [formData.bookingDate, location._id]);

  const fetchAvailableCoupons = async () => {
    try {
      const response = await axios.get(`${API}/api/coupons`);
      // Filter active coupons for this location or global coupons
      const activeCoupons = response.data.filter(coupon => 
        coupon.isActive && 
        (!coupon.locationId || coupon.locationId === location._id)
      );
      setAvailableCoupons(activeCoupons);
    } catch (error) {
      console.error('Failed to fetch coupons:', error);
    }
  };

  const fetchAvailableSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const endDate = new Date(date);
      endDate.setDate(endDate.getDate() + 7); // Get slots for next 7 days
      
      const response = await axios.get(`${API}/api/slots/available`, {
        params: {
          locationId: location._id,
          startDate: date,
          endDate: endDate.toISOString().split('T')[0]
        }
      });
      
      setAvailableSlots(response.data.slots);
      setSlotsByDate(response.data.slotsByDate);
    } catch (error) {
      console.error('Failed to fetch slots:', error);
      setErrors({ slots: 'Failed to load available slots' });
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleVerifyCoupon = async () => {
    if (!formData.couponCode.trim()) {
      setCouponDiscount(null);
      return;
    }

    try {
      const response = await axios.post(`${API}/api/bookings/verify-coupon`, {
        couponCode: formData.couponCode,
        locationId: location._id,
        amount: baseAmount
      });

      setCouponDiscount(response.data.coupon);
      setErrors({ ...errors, coupon: null });
    } catch (error) {
      setCouponDiscount(null);
      setErrors({ ...errors, coupon: error.response?.data?.error || 'Invalid coupon' });
    }
  };

  const handleVerifyAgency = async () => {
    if (!formData.agencyCode.trim()) {
      setAgencyInfo(null);
      return;
    }

    try {
      const response = await axios.post(`${API}/api/bookings/verify-agency`, {
        agencyCode: formData.agencyCode
      });

      setAgencyInfo(response.data.agency);
      setErrors({ ...errors, agency: null });
    } catch (error) {
      setAgencyInfo(null);
      setErrors({ ...errors, agency: error.response?.data?.error || 'Invalid agency code' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.customerName.trim()) newErrors.customerName = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!/^\S+@\S+\.\S+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.whatsappNumber.trim()) newErrors.whatsappNumber = 'WhatsApp number is required';
    if (!/^\+?[\d\s-]{10,}$/.test(formData.whatsappNumber)) newErrors.whatsappNumber = 'Invalid phone number';
    if (formData.numberOfJumpers < 1) newErrors.numberOfJumpers = 'At least 1 jumper required';
    if (!formData.bookingDate) newErrors.bookingDate = 'Date is required';
    if (!formData.slotId) newErrors.slotId = 'Please select a time slot';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Step 1: Create booking
      const bookingResponse = await axios.post(`${API}/api/bookings/create`, {
        ...formData,
        locationId: location._id,
        couponCode: formData.couponCode || null,
        agencyCode: formData.agencyCode || null
      });

      const booking = bookingResponse.data.booking;

      // Step 2: If agency booking (no payment required), show success
      if (booking.isAgencyBooking) {
        setBookingResult(booking);
        setSuccess(true);
        
        // Reset form
        setFormData({
          customerName: '',
          email: '',
          whatsappNumber: '',
          numberOfJumpers: 1,
          bookingDate: '',
          slotId: '',
          couponCode: '',
          agencyCode: '',
          paymentType: 'full'
        });
        setCouponDiscount(null);
        setAgencyInfo(null);
        setLoading(false);
        return;
      }

      // Step 3: Initiate payment for regular bookings
      const paymentResponse = await axios.post(`${API}/api/payment/initiate`, {
        bookingId: booking.bookingId,
        customerName: formData.customerName,
        customerEmail: formData.email,
        customerPhone: formData.whatsappNumber,
        amount: paymentAmount,
        productInfo: `${location.name} - ${formData.numberOfJumpers} Jumper(s)`
      });

      if (paymentResponse.data.success) {
        // Step 4: Redirect to Easebuzz payment page
        const paymentUrl = paymentResponse.data.data.paymentUrl;
        
        // Store booking ID in localStorage for reference
        localStorage.setItem('currentBookingId', booking.bookingId);
        
        // Redirect to payment gateway
        window.location.href = paymentUrl;
      } else {
        setErrors({ 
          submit: paymentResponse.data.message || 'Failed to initiate payment. Please try again.' 
        });
        setLoading(false);
      }
      
    } catch (error) {
      console.error('Booking error:', error);
      setErrors({ 
        submit: error.response?.data?.error || 'Failed to create booking. Please try again.' 
      });
      setLoading(false);
    }
  };

  const selectedSlot = availableSlots.find(slot => slot._id === formData.slotId);

  if (!location.bookingEnabled) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
        <AlertCircle className="mx-auto mb-3 text-red-400" size={48} />
        <h3 className="text-xl font-bold text-white mb-2">Booking Not Available</h3>
        <p className="text-gray-300">Online booking is currently not available for this location. Please contact us for bookings.</p>
      </div>
    );
  }

  if (success && bookingResult) {
    return (
      <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-8">
        <CheckCircle className="mx-auto mb-4 text-green-400" size={64} />
        <h3 className="text-2xl font-bold text-white mb-4 text-center">Booking Confirmed!</h3>
        <div className="bg-gray-800 rounded-lg p-6 mb-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-400">Booking ID</p>
              <p className="text-white font-bold">{bookingResult.bookingId}</p>
            </div>
            <div>
              <p className="text-gray-400">Name</p>
              <p className="text-white">{bookingResult.customerName}</p>
            </div>
            <div>
              <p className="text-gray-400">Location</p>
              <p className="text-white">{bookingResult.locationName}</p>
            </div>
            <div>
              <p className="text-gray-400">Date & Time</p>
              <p className="text-white">{new Date(bookingResult.bookingDate).toLocaleDateString()}</p>
              <p className="text-white text-xs">{bookingResult.slotTime}</p>
            </div>
            <div>
              <p className="text-gray-400">Jumpers</p>
              <p className="text-white">{bookingResult.numberOfJumpers}</p>
            </div>
            <div>
              <p className="text-gray-400">Total Amount</p>
              <p className="text-white font-bold">₹{bookingResult.totalAmount}</p>
            </div>
          </div>
        </div>
        <p className="text-gray-300 text-center mb-4">
          {bookingResult.isAgencyBooking 
            ? 'Your agency booking has been confirmed. No payment required.' 
            : 'A confirmation has been sent to your email and WhatsApp.'}
        </p>
        <button
          onClick={() => {
            setSuccess(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="w-full px-6 py-3 bg-yellow-400 hover:bg-yellow-500 text-black font-bold rounded-lg transition-colors"
        >
          Make Another Booking
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 lg:p-8">
      <h3 className="text-2xl font-bold text-white mb-6">Book Your Jump</h3>
      
      {pricePerJumper === 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6 flex items-start">
          <Info className="mr-3 text-yellow-400 flex-shrink-0 mt-1" size={20} />
          <p className="text-yellow-400 text-sm">Price information not set for this location. Please contact admin.</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Details */}
        <div className="space-y-4">
          <h4 className="text-lg font-bold text-yellow-400">Personal Details</h4>
          
          <div>
            <label className="block text-white font-medium mb-2">Full Name</label>
            <input
              type="text"
              value={formData.customerName}
              onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
              placeholder="Enter your full name"
            />
            {errors.customerName && <p className="text-red-400 text-sm mt-1">{errors.customerName}</p>}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-white font-medium mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  placeholder="Enter your email"
                />
              </div>
              {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-white font-medium mb-2">WhatsApp Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="tel"
                  value={formData.whatsappNumber}
                  onChange={(e) => setFormData({ ...formData, whatsappNumber: e.target.value })}
                  className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  placeholder="Your Whatsapp Number"
                />
              </div>
              {errors.whatsappNumber && <p className="text-red-400 text-sm mt-1">{errors.whatsappNumber}</p>}
            </div>
          </div>
        </div>

        {/* Booking Details */}
        <div className="space-y-4">
          <h4 className="text-lg font-bold text-yellow-400">Booking Details</h4>
          
          <div>
            <label className="block text-white font-medium mb-2">Number of Jumpers</label>
            <div className="relative">
              <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="number"
                min="1"
                max={location.maxJumpersPerSlot || 10}
                value={formData.numberOfJumpers}
                onChange={(e) => setFormData({ ...formData, numberOfJumpers: parseInt(e.target.value) })}
                className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
              />
            </div>
            {errors.numberOfJumpers && <p className="text-red-400 text-sm mt-1">{errors.numberOfJumpers}</p>}
          </div>

          <div>
            <label className="block text-white font-medium mb-2">Select Date</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="date"
                min={new Date().toISOString().split('T')[0]}
                value={formData.bookingDate}
                onChange={(e) => {
                  setFormData({ ...formData, bookingDate: e.target.value, slotId: '' });
                }}
                className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                placeholder="choose date"
              />
            </div>
            {errors.bookingDate && <p className="text-red-400 text-sm mt-1">{errors.bookingDate}</p>}
          </div>

          {formData.bookingDate && (
            <div>
              <label className="block text-white font-medium mb-2">Select Time Slot *</label>
              {loadingSlots ? (
                <p className="text-gray-400">Loading available slots...</p>
              ) : availableSlots.length === 0 ? (
                <p className="text-red-400">No available slots for this date. Please select another date.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {availableSlots.map((slot) => (
                    <button
                      key={slot._id}
                      type="button"
                      onClick={() => setFormData({ ...formData, slotId: slot._id })}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        formData.slotId === slot._id
                          ? 'border-yellow-400 bg-yellow-400/10'
                          : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Clock size={16} className="mr-2 text-yellow-400" />
                          <span className="text-white font-medium">
                            {slot.startTime} - {slot.endTime}
                          </span>
                        </div>
                        <span className={`text-sm ${
                          slot.availableSeats < 5 ? 'text-orange-400' : 'text-green-400'
                        }`}>
                          {slot.availableSeats} seats
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {errors.slotId && <p className="text-red-400 text-sm mt-1">{errors.slotId}</p>}
            </div>
          )}
        </div>

        {/* Apply Coupon */}
        <div className="space-y-4">
          <h4 className="text-lg font-bold text-yellow-400">Apply Coupon</h4>
          
          {/* Available Coupons List */}
          {availableCoupons.length > 0 && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <p className="text-blue-400 font-medium mb-2 flex items-center">
                <Tag size={16} className="mr-2" />
                Available Coupons
              </p>
              <div className="space-y-2">
                {availableCoupons.map(coupon => (
                  <div key={coupon._id} className="flex justify-between items-center text-sm">
                    <div>
                      <span className="text-white font-bold">{coupon.code}</span>
                      <span className="text-gray-400 ml-2">
                        {coupon.discountType === 'percentage' 
                          ? `${coupon.discountValue}% off` 
                          : `₹${coupon.discountValue} off`}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, couponCode: coupon.code })}
                      className="text-yellow-400 hover:text-yellow-300 text-xs"
                    >
                      Apply
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div>
            <label className="block text-white font-medium mb-2">Enter Coup code</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  value={formData.couponCode}
                  onChange={(e) => setFormData({ ...formData, couponCode: e.target.value.toUpperCase() })}
                  className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white uppercase focus:outline-none focus:border-yellow-400"
                  placeholder="COUPON"
                />
              </div>
              <button
                type="button"
                onClick={handleVerifyCoupon}
                className="px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                Apply
              </button>
            </div>
            {errors.coupon && <p className="text-red-400 text-sm mt-1">{errors.coupon}</p>}
            {couponDiscount && (
              <p className="text-green-400 text-sm mt-1">
                ✓ Coupon applied! You save ₹{couponDiscount.discountAmount}
              </p>
            )}
          </div>
        </div>

        {/* Payment Type */}
        {!agencyInfo && location.partialPaymentEnabled && location.partialPaymentAmount > 0 && (
          <div className="space-y-4">
            <h4 className="text-lg font-bold text-yellow-400">Payment Option</h4>
            <div className="grid md:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, paymentType: 'full' })}
                className={`p-4 rounded-lg border-2 transition-all ${
                  formData.paymentType === 'full'
                    ? 'border-yellow-400 bg-yellow-400/10'
                    : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                }`}
              >
                <p className="text-white font-bold">Full Payment</p>
                <p className="text-gray-300 text-sm">Pay ₹{totalAmount} now</p>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, paymentType: 'partial' })}
                className={`p-4 rounded-lg border-2 transition-all ${
                  formData.paymentType === 'partial'
                    ? 'border-yellow-400 bg-yellow-400/10'
                    : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                }`}
              >
                <p className="text-white font-bold">Partial Payment</p>
                <p className="text-gray-300 text-sm">Pay ₹{location.partialPaymentAmount} now, ₹{pendingAmount} later</p>
              </button>
            </div>
          </div>
        )}

        {/* Price Summary */}
        <div className="bg-gray-900 rounded-lg p-6 space-y-3">
          <h4 className="text-lg font-bold text-white mb-4">Price Summary</h4>
          <div className="flex justify-between text-gray-300">
            <span>Base Amount {formData.numberOfJumpers} x ₹{pricePerJumper}</span>
            <span>₹{baseAmount}</span>
          </div>
          {discountAmount > 0 && (
            <div className="flex justify-between text-green-400">
              <span>Discount</span>
              <span>- ₹{discountAmount}</span>
            </div>
          )}
          <div className="border-t border-gray-700 pt-3">
            <div className="flex justify-between text-white font-bold text-xl">
              <span>Total Amount</span>
              <span>₹{totalAmount}</span>
            </div>
            {!agencyInfo && formData.paymentType === 'partial' && location.partialPaymentEnabled && (
              <div className="mt-2 space-y-1 text-sm">
                <div className="flex justify-between text-yellow-400">
                  <span>Pay Now</span>
                  <span>₹{paymentAmount}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Pay at Venue</span>
                  <span>₹{pendingAmount}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {errors.submit && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <p className="text-red-400 flex items-center">
              <AlertCircle size={20} className="mr-2" />
              {errors.submit}
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || pricePerJumper === 0}
          className="w-full px-6 py-4 bg-yellow-400 hover:bg-yellow-500 text-black font-bold text-lg rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {loading ? (
            'Processing...'
          ) : (
            <>
              <CreditCard size={20} className="mr-2" />
              {agencyInfo ? 'Confirm Booking' : 'Book Now'}
            </>
          )}
        </button>

        <p className="text-gray-400 text-sm text-center">
          By booking, you agree to our terms and conditions
        </p>
      </form>
    </div>
  );
};

export default BookingForm;
